// Helper to clamp duration between 2000 and 10000ms, but allow 0 for manual-only
function clampDuration(duration?: number): number {
  if (duration === 0) {
    return 0;
  }
  if (typeof duration !== 'number' || isNaN(duration)) {
    return 4000;
  }
  return Math.max(2000, Math.min(10000, duration));
}
/**
 * Harmony Snackbar Service
 *
 * Injectable service (providedIn: 'root') for programmatic snackbar creation. Follows Angular Material architecture
 * with CDK Overlay for positioning.
 *
 * @example
 *   ```typescript
 *   import { Component, inject } from '@angular/core';
 *   import { HxSnackbarService } from '@complexcare/harmony';
 *
 *   @Component({...})
 *   export class MyComponent {
 *     private snackbar = inject(HxSnackbarService);
 *
 *     showNotification() {
 *       this.snackbar.show('Document saved successfully');
 *     }
 *
 *     saveDocument() {
 *       // ... save logic
 *       this.snackbar.success('Document saved successfully');
 *     }
 *
 *     handleError(error: Error) {
 *       this.snackbar.error('Failed to save: ' + error.message);
 *     }
 *   }
 *   ```;
 */
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ApplicationRef, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { HxSnackbarConfig, isValidSnackbarConfig } from './snackbar-config';
import { HxSnackbarContainer } from './snackbar-container';
import { HxSnackbarRef } from './snackbar-ref';

@Injectable({
  providedIn: 'root',
})
export class HxSnackbarService {
  /** CDK Overlay service */
  private overlay = inject(Overlay);

  /** Application ref for component creation */
  private appRef = inject(ApplicationRef);

  /** Environment injector */
  private injector = inject(EnvironmentInjector);

  /** Singleton container instance */
  private containerRef: OverlayRef | null = null;
  private containerInstance: HxSnackbarContainer | null = null;

  /** Show a snackbar with configuration */
  show(config: HxSnackbarConfig): HxSnackbarRef;
  show(message: string, config?: Partial<HxSnackbarConfig>): HxSnackbarRef;
  show(configOrMessage: HxSnackbarConfig | string, partialConfig?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    // Normalize to full config
    let config: HxSnackbarConfig;

    if (typeof configOrMessage === 'string') {
      config = {
        message: configOrMessage,
        ...partialConfig,
      };
    } else {
      config = configOrMessage;
    }

    // Validate config
    if (!isValidSnackbarConfig(config)) {
      throw new Error('Invalid snackbar configuration: message is required and must be non-empty');
    }

    // Ensure container exists. Recreate if the requested position has changed.
    const requestedPosition = config.position || 'bottom-right';
    if (!this.containerInstance || !this.containerRef || this.containerInstance.position !== requestedPosition) {
      if (this.containerRef) {
        this.containerRef.dispose();
        this.containerRef = null;
        this.containerInstance = null;
      }
      this.createContainer(config);
    }

    // Create snackbar reference using container's overlay
    const snackbarRef = new HxSnackbarRef(this.containerRef!, this.containerInstance);

    // Clamp duration and provide message fallback before attaching
    const fallbackMessage = config.message && config.message.trim().length > 0 ? config.message : 'Notification';
    const clampedConfig = {
      ...config,
      duration: clampDuration(config.duration),
      message: fallbackMessage,
      announcementMessage: config.announcementMessage || fallbackMessage,
      maxVisible: typeof config.maxVisible === 'number' && config.maxVisible > 0 ? config.maxVisible : 10,
    };
    if (this.containerInstance) {
      this.containerInstance.attach(clampedConfig, snackbarRef);
    }

    return snackbarRef;
  }

  /**
   * Show a success notification (positive context)
   *
   * @param message - Message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  success(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.show({
      message,
      variant: 'soft',
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'positive',
    });
  }

  /**
   * Show an error notification (negative context)
   *
   * @param message - Error message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  error(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.show({
      message,
      variant: 'soft',
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'negative',
    });
  }

  /**
   * Show a warning notification (caution context)
   *
   * @param message - Warning message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  warning(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.show({
      message,
      variant: 'soft',
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'caution',
    });
  }

  /**
   * Show an informational notification (info context)
   *
   * @param message - Information message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  info(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.show({
      message,
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'info',
    });
  }

  /** Dismiss a specific snackbar */
  dismiss(ref: HxSnackbarRef): void {
    ref.dismiss();
  }

  /** Dismiss all active snackbars */
  dismissAll(): void {
    if (this.containerInstance) {
      this.containerInstance.dismissAll();
    }
  }

  /** Create the singleton container */
  private createContainer(config?: HxSnackbarConfig): void {
    // Determine position from config or default
    const position = config?.position || 'bottom-right';

    // RTL auto-mirroring
    const isRtl = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
    let horizontal: 'left' | 'right' | 'center';
    let vertical: 'top' | 'bottom';
    const offsetY = '16px';
    const offsetX = '16px';

    switch (position) {
      case 'top-left':
        vertical = 'top';
        horizontal = isRtl ? 'right' : 'left';
        break;
      case 'top-right':
        vertical = 'top';
        horizontal = isRtl ? 'left' : 'right';
        break;
      case 'bottom-left':
        vertical = 'bottom';
        horizontal = isRtl ? 'right' : 'left';
        break;
      case 'bottom-right':
        vertical = 'bottom';
        horizontal = isRtl ? 'left' : 'right';
        break;
      case 'top-center':
        vertical = 'top';
        horizontal = 'center';
        break;
      case 'bottom-center':
        vertical = 'bottom';
        horizontal = 'center';
        break;
      default:
        vertical = 'top';
        horizontal = isRtl ? 'left' : 'right';
    }

    let positionStrategy = this.overlay.position().global();
    if (vertical === 'top') {
      positionStrategy = positionStrategy.top(offsetY);
    } else {
      positionStrategy = positionStrategy.bottom(offsetY);
    }
    if (horizontal === 'center') {
      positionStrategy = positionStrategy.centerHorizontally();
    } else if (horizontal === 'left') {
      positionStrategy = positionStrategy.left(offsetX);
    } else {
      positionStrategy = positionStrategy.right(offsetX);
    }

    this.containerRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: ['hx-snackbar-container-overlay', position],
    });

    // Create container component
    const containerPortal = new ComponentPortal(HxSnackbarContainer, null, this.injector);
    const containerComponentRef = this.containerRef.attach(containerPortal);
    this.containerInstance = containerComponentRef.instance;
  }
}
